import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, Wallet, BadgeDollarSign, PiggyBank, Send } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const AuthorEarnings = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [paypal, setPaypal] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ["author-earnings"],
    queryFn: async () => (await api.get("/author/earnings")).data,
  });
  if (!data) return null;

  const openDialog = () => {
    setPaypal(data.paypal_email || "");
    setAmount(data.balance > 0 ? String(data.balance) : "");
    setOpen(true);
  };

  const submitRequest = async () => {
    setBusy(true);
    try {
      const res = await api.post("/author/payout-requests", { paypal_email: paypal, amount });
      toast.success(res.data.message);
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["author-earnings"] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const cards = [
    { icon: Eye, label: "Total views", value: data.total_views.toLocaleString() },
    { icon: BadgeDollarSign, label: "Accrued", value: `$${data.accrued.toFixed(2)}` },
    { icon: Wallet, label: "Paid out", value: `$${data.paid.toFixed(2)}` },
    { icon: PiggyBank, label: "Balance", value: `$${data.balance.toFixed(2)}` },
  ];

  return (
    <div data-testid="author-earnings" className="mt-8 bg-[#0d1117] border border-[#22c55e]/25 rounded-xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading font-bold text-white text-lg">Your earnings</h2>
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">
            Rate: ${data.rate_per_1k.toFixed(2)} per 1,000 views
          </p>
        </div>
        {data.pending_request ? (
          <span data-testid="pending-request-badge"
            className="font-mono text-[10px] uppercase tracking-widest text-yellow-400 border-l-2 border-yellow-400 pl-2 py-1">
            ${data.pending_request.amount.toFixed(2)} withdrawal pending → {data.pending_request.paypal_email}
          </span>
        ) : (
          <Button onClick={openDialog} disabled={data.balance < 1} data-testid="request-payout-button"
            className="bg-[#22c55e] text-black font-bold hover:bg-[#1ea34d]">
            <Send className="h-4 w-4 mr-1.5" /> Request payout
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="bg-[#121212] border border-white/10 rounded-lg p-3.5">
            <p className="font-mono text-[9px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
              <c.icon className="h-3 w-3 text-[#22c55e]" /> {c.label}
            </p>
            <p className="font-mono text-xl font-bold text-white mt-1" data-testid={`earnings-${c.label.toLowerCase().replace(" ", "-")}`}>{c.value}</p>
          </div>
        ))}
      </div>
      {(data.articles || []).length > 0 && (
        <div className="mt-4 space-y-1.5">
          {data.articles.slice(0, 5).map((a: any) => (
            <div key={a.slug} className="flex items-center justify-between font-mono text-xs">
              <span className="text-gray-400 truncate pr-3">{a.title}</span>
              <span className="text-gray-500 shrink-0">{a.views.toLocaleString()} views</span>
            </div>
          ))}
        </div>
      )}
      {(data.payouts || []).length > 0 && (
        <div className="mt-4 border-t border-white/10 pt-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Payout history</p>
          {data.payouts.slice(0, 5).map((p: any, i: number) => (
            <div key={i} className="flex items-center justify-between font-mono text-xs py-1">
              <span className="text-[#22c55e]">${p.amount.toFixed(2)}{p.note ? ` — ${p.note}` : ""}</span>
              <span className="text-gray-600">{timeAgo(p.created_at)}</span>
            </div>
          ))}
        </div>
      )}
      <p className="font-mono text-[10px] text-gray-600 mt-4">
        Earnings accrue on published stories at the current rate. Request a withdrawal any time your balance is $1+ — we pay via PayPal.
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Request a withdrawal</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="font-mono text-xs text-gray-400">
              Available balance: <span className="text-[#22c55e] font-bold">${data.balance.toFixed(2)}</span>
            </p>
            <div>
              <Label>PayPal email</Label>
              <Input type="email" value={paypal} onChange={(e) => setPaypal(e.target.value)}
                placeholder="you@paypal.com" data-testid="payout-paypal-input"
                className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
            </div>
            <div>
              <Label>Amount (USD)</Label>
              <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                data-testid="payout-request-amount" className="mt-1 bg-[#1A1A1A] border-white/10 text-white font-mono" />
            </div>
            <Button onClick={submitRequest} disabled={busy || !paypal || !amount} data-testid="payout-request-submit"
              className="bg-[#22c55e] text-black font-bold hover:bg-[#1ea34d] w-full">
              {busy ? "Submitting…" : "Submit withdrawal request"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
